import { message } from 'antd'
import { callApi } from './callApi'
import { markCallOrderEnded } from './callOrderLifecycleGuard'
import { shengwangClient } from './shengwangClient'
import { useCallStore } from '@/store/callStore'
import { usePresenceStore } from '@/store/presenceStore'

const OUTGOING_CALL_TIMEOUT_MS = 30000

let outgoingTimeout: { orderID: string; timer: number } | null = null

export function resolveOutgoingCallDeadlineMs(expireAt?: number, now = Date.now()) {
  if (!expireAt || !Number.isFinite(expireAt)) {
    return now + OUTGOING_CALL_TIMEOUT_MS
  }
  if (expireAt > 1_000_000_000_000) {
    return expireAt
  }
  if (expireAt > 1_000_000_000) {
    return expireAt * 1000
  }
  return now + expireAt * 1000
}

function resolveOutgoingCallTimeoutMs(expireAt?: number) {
  return Math.max(0, resolveOutgoingCallDeadlineMs(expireAt) - Date.now())
}

export function scheduleOutgoingCallTimeout(orderID: string, expireAt?: number) {
  clearOutgoingCallTimeout()
  if (!orderID) {
    return
  }
  const timer = window.setTimeout(() => {
    const latest = useCallStore.getState().outgoingCall
    if (!latest?.order_id || latest.order_id !== orderID || latest.status !== 'ringing') {
      clearOutgoingCallTimeout(orderID)
      return
    }
    void releaseOutgoingCall(orderID, 'call_timeout', 'user_answer_timeout', {
      notify: true,
      warning: 'The user did not answer. The call timed out.',
    })
  }, resolveOutgoingCallTimeoutMs(expireAt))
  outgoingTimeout = { orderID, timer }
}

export function clearOutgoingCallTimeout(orderID?: string) {
  if (!outgoingTimeout) {
    return
  }
  if (orderID && outgoingTimeout.orderID !== orderID) {
    return
  }
  window.clearTimeout(outgoingTimeout.timer)
  outgoingTimeout = null
}

export async function releaseOutgoingCall(
  orderID: string,
  eventType: 'call_rejected' | 'call_timeout' | 'call_canceled',
  reasonCode: string,
  options?: { notify?: boolean; warning?: string }
) {
  if (!orderID) {
    return
  }
  markCallOrderEnded(orderID)
  clearOutgoingCallTimeout(orderID)
  const calls = useCallStore.getState()
  if (calls.outgoingCall?.order_id === orderID) {
    calls.setOutgoingCall(null)
  }
  const presence = usePresenceStore.getState()
  if (!calls.currentCall || calls.currentCall.order_id === orderID) {
    presence.setCallStatus('idle')
  }
  await shengwangClient.leaveCallForOrder(orderID).catch(() => undefined)
  try {
    await callApi.releaseCall(orderID, {
      event_type: eventType,
      reason_code: reasonCode,
    })
  } catch {
    // Release is best-effort for this frontend phase; backend TTL remains the final safety net.
  }
  if (options?.notify && options.warning) {
    message.warning(options.warning)
  }
}
